<template>
  <Modal variant="task" @close="close">
    <template slot="content">
      <div class="p-tasklist-form">
        <div v-if="isEditMode" class="p-tasklist-form__header">
          <h2 class="p-tasklist-form__title">
            #{{ item.id }}
            <span v-if="taskUpdatedDate" class="c-text--note">
              タスク編集日: {{ taskUpdatedDate }}
            </span>
            <Button
              radius
              variant="trash"
              :disabled="isDeleting || isProcessing"
              @click="deleteTask"
            >
              <Tooltip variant="btn">
                タスクを削除
              </Tooltip>
            </Button>
          </h2>
        </div>
        <div class="p-tasklist-form__content">
          <div class="c-fieldset c-fieldset--wrap">
            <div class="c-fieldset__header">
              <p class="c-fieldset__title">施策内容を選択する</p>
            </div>
            <div class="c-fieldset__content">
              <Select
                v-model="params.type"
                :options="types"
                placeholder="タスクの施策内容を選択してください"
                :selected="params.type"
                required
                data-testid="input-type"
              />
            </div>
          </div>
          <div class="c-fieldset c-fieldset--wrap">
            <div class="c-fieldset__header">
              <p class="c-fieldset__title">タスクに担当者を設定する</p>
            </div>
            <div class="c-fieldset__content c-alertPos">
              <Select
                v-model="params.operator"
                :options="projectUsers"
                placeholder="担当者なし"
                :selected="params.operator"
                :class="{
                  alert: error.operator
                }"
              />
              <Alert v-if="error.operator">
                {{ error.operator }}
              </Alert>
            </div>
          </div>
          <div class="c-fieldset c-fieldset--wrap">
            <div class="c-fieldset__header">
              <p class="c-fieldset__title">
                詳細<span class="c-required">必須</span>
              </p>
            </div>
            <div class="c-fieldset__content c-alertPos">
              <Textarea
                v-model="params.text"
                v-focus
                placeholder="タスクの詳細を記入してください"
                :class="{
                  alert: isEdited.text && error.text
                }"
                data-testid="input-text"
              />
              <Alert v-if="isEdited.text && error.text">
                {{ error.text }}
              </Alert>
            </div>
          </div>
          <template v-if="params.type">
            <div class="c-fieldset c-fieldset--wrap">
              <div class="c-fieldset__header">
                <p class="c-fieldset__title">
                  施策を実施するキーワードを設定する<span
                    v-if="isChangeTd || isRewrite || isCreateContent"
                    class="c-required"
                    >必須</span
                  >
                </p>
              </div>
              <div class="c-fieldset__content c-alertPos">
                <Input
                  v-model="params.keyword"
                  placeholder="キーワードを入力してください"
                  size="sm"
                  :class="{
                    alert: isEdited.keyword && error.keyword
                  }"
                  @focusout="focusoutKeyword"
                  data-testid="input-keyword"
                />
                <Alert v-if="isEdited.keyword && error.keyword">
                  {{ error.keyword }}
                </Alert>
              </div>
            </div>
            <div class="c-fieldset c-fieldset--wrap">
              <div class="c-fieldset__header">
                <p class="c-fieldset__title">
                  施策を実施するページURLを登録する<span
                    v-if="isChangeTd || isRewrite || isSeoCheckUp"
                    class="c-required"
                    >必須</span
                  >
                </p>
              </div>
              <div class="c-fieldset__content c-alertPos">
                <InputUrl :url="project.siteUrl">
                  <Input
                    v-model="params.url"
                    placeholder="/"
                    size="sm"
                    :class="{
                      alert: isEdited.url && error.url
                    }"
                    @focusout="focusoutUrl"
                    data-testid="input-url"
                  />
                  <Alert v-if="isEdited.url && error.url">
                    {{ error.url }}
                  </Alert>
                </InputUrl>
              </div>
            </div>
          </template>
          <div class="c-fieldset c-fieldset--wrap">
            <div class="c-fieldset__header">
              <p class="c-fieldset__title">施策完了予定日</p>
            </div>
            <div class="c-fieldset__content c-alertPos">
              <DatePicker v-model="params.executionDate" :max-date="null" />
              <Alert v-if="error.executionDate">
                {{ error.executionDate }}
              </Alert>
            </div>
          </div>
          <div v-if="isEditMode" class="c-fieldset c-fieldset--wrap">
            <div class="c-fieldset__header">
              <p class="c-fieldset__title">施策完了日</p>
            </div>
            <div class="c-fieldset__content c-alertPos">
              <DatePicker v-model="params.typeDoneDate" />
              <Alert v-if="error.typeDoneDate">
                {{ error.typeDoneDate }}
              </Alert>
            </div>
          </div>
        </div>
        <div class="p-tasklist-form__footer">
          <Button class="p-tasklist-form__cancel" @click="close">
            <Icon2 variant="close" :size="10" />
            キャンセル
          </Button>
          <div class="c-alertPos">
            <Button
              variant="main"
              size="w230"
              fsize="f16"
              icon="plus"
              :disabled="isDisabled"
              :class="{ 'is-loading': isProcessing }"
              @click="submit"
              data-testid="button-submit"
            >
              {{ isEditMode ? 'タスクを更新する' : 'タスクを登録する' }}
            </Button>
            <Alert v-if="errorResponse">{{ errorResponse }}</Alert>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import { ERROR_MESSAGE, TASK_LENGTHS_LIMIT } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import DatePicker from '@/components/DatePicker';
import Icon2 from '@/components/Icon2';
import Input from '@/components/Input';
import InputUrl from '@/components/InputUrl';
import Modal from '@/components/Modal';
import Select from '@/components/Select';
import Textarea from '@/components/Textarea';
import Tooltip from '@/components/Tooltip';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: {
    column: {
      type: String,
      required: false
    },
    item: {
      type: Object,
      required: false
    },
    mode: {
      type: String,
      required: false
    },
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    Alert,
    Button,
    DatePicker,
    Icon2,
    Input,
    InputUrl,
    Modal,
    Select,
    Textarea,
    Tooltip
  },
  data() {
    return {
      error: {
        keyword: '',
        operator: '',
        text: '',
        type: '',
        url: '',
        executionDate: '',
        typeDoneDate: ''
      },
      errorResponse: '',
      params: {
        keyword: this.item.keyword || '',
        operator: this.item.operatorUserId || '',
        text: this.item.taskDetail || '',
        type: this.item.type || '',
        executionDate: this.item.executionDate
          ? new Date(this.item.executionDate)
          : null,
        typeDoneDate: this.item.typeDoneDate
          ? new Date(this.item.typeDoneDate)
          : null,
        url: this.$options.filters.removeDomain(
          this.item.url,
          this.project.siteUrl
        )
      },
      isDeleting: false,
      isEdited: {
        keyword: false,
        operator: false,
        text: false,
        type: false,
        url: false,
        executionDate: false,
        typeDoneDate: false
      },
      isEditMode: this.mode === 'edit',
      isProcessing: false
    };
  },
  computed: {
    category() {
      let category = '';
      if (this.categoryLabel[this.item.type]) {
        category = this.categoryLabel[this.item.type];
      }
      return category;
    },
    categoryLabel() {
      return this.$store.getters['task/solutionLabel'];
    },
    projectUsers() {
      const users = [...this.$store.getters['project/usersSelect']];
      // 選択中の担当者が存在しない && 担当者を変更していない場合は、セレクトボックスに選択中の担当者を追加する
      if (
        this.item.operatorUserId &&
        !this.existOperator(this.item.operatorUserId) &&
        !this.isEdited.operator
      ) {
        users.push({
          label: this.item.operatorUserName,
          value: this.item.operatorUserId
        });
      }
      return users;
    },
    projectUsersName() {
      return this.$store.getters['project/usersName'];
    },
    taskUpdatedDate() {
      return this.item.updatedAt
        ? utils.formatDate(this.item.updatedAt, 'YYYY.MM.DD')
        : null;
    },
    text() {
      const array = this.item.taskDetail.split('\n');
      const title = array.shift();
      const body = array.join('\n');
      return {
        title: title,
        body: body
      };
    },
    types() {
      return this.$store.getters['task/solutionSelect'];
    },
    isChangeTd() {
      return this.params.type === 'changeTd';
    },
    isCreateContent() {
      return this.params.type === 'createContent';
    },
    isRewrite() {
      return this.params.type === 'rewrite';
    },
    isSeoCheckUp() {
      return this.params.type === 'seoCheckUp';
    },
    isOther() {
      return this.params.type === 'other';
    },
    isDisabled() {
      return (
        this.isError ||
        this.isProcessing ||
        this.isDeleting ||
        !(
          this.isEdited.keyword ||
          this.isEdited.operator ||
          this.isEdited.text ||
          this.isEdited.type ||
          this.isEdited.url ||
          this.isEdited.executionDate ||
          this.isEdited.typeDoneDate
        )
      );
    },
    isError() {
      if (
        this.error.text ||
        this.error.type ||
        this.error.operator ||
        this.error.keyword ||
        this.error.url
      ) {
        return true;
      }
      return false;
    }
  },
  watch: {
    params: {
      handler() {
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.errorResponse = '';
      },
      deep: true
    },
    'params.keyword': {
      handler(value, oldValue) {
        const keyword = value.trim();
        this.validateKeyword(keyword);

        if (this.isEditMode && this.item.keyword) {
          const keyword = this.params.keyword.trim();
          this.isEdited.keyword = keyword !== this.item.keyword;
        } else if (typeof oldValue !== 'undefined') {
          this.isEdited.keyword = true;
        }
      },
      immediate: true
    },
    'params.operator': {
      handler(value, oldValue) {
        const operator = value;
        if (operator && !this.existOperator(operator)) {
          this.error.operator =
            '選択したユーザーが存在しません。担当者を再度設定してください';
        } else {
          this.error.operator = '';
        }
        if (this.isEditMode) {
          const oldOperator = this.item.operatorUserId || '';
          this.isEdited.operator = operator !== oldOperator;
        } else if (typeof oldValue !== 'undefined') {
          this.isEdited.operator = true;
        }
      },
      immediate: true
    },
    'params.text': {
      handler(value, oldValue) {
        const text = value.trim();
        this.error.text = new Validation()
          .input()
          .maxLength(TASK_LENGTHS_LIMIT)
          .test(text);

        if (this.isEditMode) {
          this.isEdited.text = text !== this.item.taskDetail;
        } else if (typeof oldValue !== 'undefined') {
          this.isEdited.text = true;
        }
      },
      immediate: true
    },
    'params.type': {
      handler(value, oldValue) {
        const type = value;
        this.error.type = new Validation().select().test(type);
        if (this.isEditMode) {
          this.isEdited.type = type !== this.item.type;
        } else if (typeof oldValue !== 'undefined') {
          this.isEdited.type = true;
        }
        const keyword = this.params.keyword.trim();
        this.validateKeyword(keyword);
        const url = this.params.url.trim();
        this.validateUrl(url);
      },
      immediate: true
    },
    'params.url': {
      handler(value, oldValue) {
        const url = value.trim();
        this.validateUrl(url);

        if (this.isEditMode && this.item.url) {
          const oldUrl = this.$options.filters.removeDomain(
            this.item.url,
            this.project.siteUrl
          );
          this.isEdited.url = url !== oldUrl;
        } else if (typeof oldValue !== 'undefined') {
          this.isEdited.url = true;
        }
      },
      immediate: true
    },
    'params.executionDate': {
      handler(value, oldValue) {
        if (this.item.executionDate) {
          this.isEdited.executionDate =
            utils.formatDate(value, 'YYYY-MM-DD') !=
            utils.formatDate(this.item.executionDate, 'YYYY-MM-DD');
        } else {
          this.isEdited.executionDate = !!value;
        }
      },
      immediate: true
    },
    'params.typeDoneDate': {
      handler(value, oldValue) {
        if (this.item.typeDoneDate) {
          this.isEdited.typeDoneDate =
            utils.formatDate(value, 'YYYY-MM-DD') !=
            utils.formatDate(this.item.typeDoneDate, 'YYYY-MM-DD');
        } else {
          this.isEdited.typeDoneDate = !!value;
        }
      },
      immediate: true
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    submit() {
      if (this.isEditMode) {
        this.editTask();
      } else {
        this.addTask();
      }
    },
    async addTask() {
      if (this.isDisabled) {
        return;
      }
      this.isProcessing = true;
      this.errorResponse = '';

      // 利用量チェック
      await this.getUsages();
      if (this.errorResponse) {
        this.isProcessing = false;
        return;
      }
      // ユーザーの存在チェック
      if (this.params.operator) {
        await this.getUsers();
        if (this.error.operator) {
          this.isProcessing = false;
          return;
        }
      }
      await this.postTask();

      this.isProcessing = false;
    },
    deleteTask() {
      if (this.isDeleting || this.isProcessing) {
        return;
      }
      this.isDeleting = true;

      apiProjects.tasks
        .delete(this.auth.idToken, this.project.id, this.item.id)
        .then(response => {
          this.$emit('delete', this.item);
          this.isDeleting = false;
        })
        .catch(error => {
          this.isDeleting = false;
        });
    },
    async editTask() {
      if (this.isDisabled) {
        return;
      }
      this.isProcessing = true;
      this.errorResponse = '';

      // ユーザーの存在チェック
      if (this.params.operator) {
        await this.getUsers();
        if (this.error.operator) {
          this.isProcessing = false;
          return;
        }
      }
      await this.putTask();

      this.isProcessing = false;
    },
    existOperator(operator) {
      return this.$store.getters['project/usersSelect'].some(
        user => user.value === operator
      );
    },
    focusoutKeyword() {
      this.params.keyword = this.$options.filters.normalizeAllSpaces(
        this.params.keyword.trim()
      );
    },
    focusoutUrl() {
      this.params.url = this.$options.filters.normalizeAllSpaces(
        this.params.url
      );
      // 先頭が/じゃないURLが入力されていたら、スラッシュを付ける
      if (this.params.url && !/^\//.test(this.params.url)) {
        this.params.url = '/' + this.params.url;
      }
    },
    validateKeyword(keyword) {
      if (this.isChangeTd || this.isCreateContent || this.isRewrite) {
        this.error.keyword = new Validation()
          .input()
          .keyword()
          .test(keyword);
      } else if (this.isOther || this.isSeoCheckUp) {
        this.error.keyword = new Validation().keyword().test(keyword);
      }
    },
    validateUrl(url) {
      if (this.isChangeTd || this.isSeoCheckUp || this.isRewrite) {
        this.error.url = new Validation().input().test(url);
      } else {
        this.error.url = '';
      }
    },
    getFullUrl(url) {
      if (!url) {
        return url;
      }
      return (
        this.$options.filters.removeTrailingSlash(this.project.siteUrl) +
        '/' +
        this.$options.filters.removeLeadingSlash(url)
      );
    },
    getTaskParams() {
      const params = {
        column: null,
        keyword: null,
        operatorUserId: this.params.operator || null,
        position: null,
        taskDetail: this.params.text,
        type: this.params.type,
        url: null,
        executionDate: this.params.executionDate
          ? utils.formatDate(this.params.executionDate, 'YYYY-MM-DD')
          : null,
        updatedAt: this.item.updatedAt
      };
      if (this.isEditMode) {
        params.typeDoneDate = this.params.typeDoneDate
          ? utils.formatDate(this.params.typeDoneDate, 'YYYY-MM-DD')
          : null;
      }
      params.keyword = this.params.keyword ? this.params.keyword : null;
      params.url = this.params.url ? this.getFullUrl(this.params.url) : null;

      return params;
    },
    getUsages() {
      const params = {
        items: 'tasks'
      };
      return apiProjects.usages
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          const usages = response.data.items[0];
          const error = utils.getErrorUsages({
            usages: usages,
            target: 'タスク'
          });
          if (error.message) {
            this.errorResponse = error.message;
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });
    },
    getUsers() {
      return this.$store.dispatch('project/getUsers').finally(() => {
        if (!this.existOperator(this.params.operator)) {
          this.error.operator =
            '選択したユーザーが存在しません。担当者を再度設定してください';
        }
      });
    },
    postTask() {
      const params = this.getTaskParams();
      params.column = this.column;

      return apiProjects.tasks.siteAnalysis
        .post(params, this.auth.idToken, this.project.id)
        .then(response => {
          const item = {
            column: this.column,
            id: response.data.id,
            keyword: params.keyword,
            operatorUserId: this.params.operator,
            operatorUserName: this.projectUsersName[this.params.operator],
            taskDetail: this.params.text,
            type: this.params.type,
            updatedAt: response.data.updatedAt,
            url: params.url,
            executionDate: this.params.executionDate
          };
          this.$emit('add', item);
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });
    },
    putTask() {
      const params = this.getTaskParams();

      return apiProjects.tasks
        .put(params, this.auth.idToken, this.project.id, this.item.id)
        .then(response => {
          const item = Object.assign({}, this.item);
          item.keyword = params.keyword;
          item.operatorUserId = this.params.operator;
          item.operatorUserName = this.projectUsersName[this.params.operator];
          item.taskDetail = this.params.text;
          item.type = this.params.type;
          item.url = params.url;
          item.executionDate = params.executionDate;
          item.typeDoneDate = params.typeDoneDate;
          item.updatedAt = response.data.updatedAt;
          this.$emit('edit', item);
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });
    }
  }
};
</script>
